import { motion } from "framer-motion";
import { Quote } from "lucide-react";
import { SEO } from "../components/SEO";
import { CTABand, StarRating } from "../components/Shared";
import { REVIEWS, GOOGLE, FEATURED_QUOTES } from "../lib/data";

export default function Reviews() {
  return (
    <>
      <SEO
        title="Reviews"
        description="See what Hamilton-area families and businesses say about Wisp Home Cleaning's residential and small-office cleaning services."
        image="/life-coffee.jpg"
      />
      <section className="border-b border-slate-200 bg-white px-5 pt-16 pb-14 md:px-10">
        <div className="mx-auto grid max-w-7xl items-center gap-10 lg:grid-cols-2">
          <div>
            <span className="text-xs font-semibold uppercase tracking-[0.2em] text-primary">Reviews</span>
            <h1 className="mt-3 max-w-3xl font-heading text-4xl font-light tracking-tight text-slate-900 sm:text-5xl lg:text-6xl">
              Kind words from the homes we care for
            </h1>
            <div className="mt-6 flex flex-wrap items-center gap-4">
              <div className="flex items-center gap-3">
                <StarRating />
                <span className="text-slate-700">
                  <span className="font-semibold text-slate-900">{GOOGLE.rating}</span> from {GOOGLE.count} Google reviews
                </span>
              </div>
              <a
                href={GOOGLE.url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="reviews-google-button"
                className="inline-flex items-center gap-2 rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground transition-all duration-300 hover:-translate-y-0.5"
              >
                Read our reviews on Google
              </a>
            </div>
          </div>
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
            className="overflow-hidden rounded-3xl border border-slate-200 shadow-lg"
          >
            <img
              src="/life-coffee.jpg"
              alt="A happy customer relaxing in her clean, tidy home"
              className="h-[300px] w-full object-cover lg:h-[360px]"
            />
          </motion.div>
        </div>
      </section>

      <section className="border-b border-slate-200 bg-white px-5 py-16 md:px-10">
        <div className="mx-auto grid max-w-7xl gap-6 md:grid-cols-2">
          {FEATURED_QUOTES.map((f, i) => (
            <motion.blockquote
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.1 }}
              data-testid={`featured-quote-${i}`}
              className="relative rounded-3xl bg-primary p-8 md:p-10"
            >
              <Quote className="h-10 w-10 text-white/30" />
              <p className="mt-4 font-heading text-2xl font-light leading-snug tracking-tight text-white sm:text-3xl">
                "{f.quote}"
              </p>
              <footer className="mt-6 text-sm font-semibold uppercase tracking-widest text-white/80">
                — {f.name}
              </footer>
            </motion.blockquote>
          ))}
        </div>
      </section>

      <section className="px-5 py-20 md:px-10">
        <div className="mx-auto grid max-w-7xl gap-6 md:grid-cols-2 lg:grid-cols-3">
          {REVIEWS.map((r, i) => (
            <motion.article
              key={r.name}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: (i % 3) * 0.06 }}
              data-testid={`review-card-${i}`}
              className="relative rounded-3xl border border-slate-200 bg-white p-7 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl"
            >
              <Quote className="absolute right-6 top-6 h-8 w-8 text-primary/15" />
              <StarRating count={r.rating} />
              <p className="mt-4 leading-relaxed text-slate-700">"{r.text}"</p>
              <div className="mt-6 flex items-center gap-3">
                <div className="flex h-11 w-11 items-center justify-center rounded-full bg-primary/10 font-heading font-semibold text-primary">
                  {r.name.charAt(0)}
                </div>
                <div>
                  <p className="font-semibold text-slate-900">{r.name}</p>
                  <p className="text-sm text-slate-500">{r.location}</p>
                </div>
              </div>
            </motion.article>
          ))}
        </div>
        <p className="mx-auto mt-12 max-w-2xl text-center text-sm text-slate-400">
          All reviews are from verified Google customers.{" "}
          <a href={GOOGLE.url} target="_blank" rel="noopener noreferrer" className="font-medium text-primary hover:underline">
            See all {GOOGLE.count} on Google
          </a>
          .
        </p>
      </section>

      <CTABand />
    </>
  );
}
